const DRAWER_QUERY = '(max-width: 1199px)';
const PHONE_QUERY = '(max-width: 749px)';
const HEADER_SELECTOR = '[data-rsv-architectural-header="true"]';
const DRAWER_SELECTOR = '.header-drawer--rsv-mega details';
const STATE_ATTRIBUTE = 'data-rsv-mobile-chrome';
const CTA_HOLD_ATTRIBUTE = 'data-rsv-mobile-cta-hold';
const PAGE_LOCK_ATTRIBUTE = 'data-rsv-mobile-menu-locked';
const INIT_VERSION = '2026-08-12.1-viewport-drawer';
const HEADER_REVEAL_DELAY_MS = 240;
const DRAWER_CLOSE_DELAY_MS = 240;

function initMobileNavigation(header) {
  if (
    !(header instanceof HTMLElement) ||
    header.dataset.rsvMobileNavigationInitialized === INIT_VERSION
  ) {
    return;
  }

  const section = header.closest('.header-section');
  if (!(section instanceof HTMLElement)) return;

  header.dataset.rsvMobileNavigationInitialized = INIT_VERSION;

  const drawerQuery = window.matchMedia(DRAWER_QUERY);
  const phoneQuery = window.matchMedia(PHONE_QUERY);
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const drawer = section.querySelector(DRAWER_SELECTOR);
  const drawerHead = drawer?.querySelector('.rsv-mega-drawer__head');
  const drawerScrollArea = drawer?.querySelector('.rsv-mega-drawer__scrollarea');
  const drawerRoot = drawer?.closest('[data-rsv-mega-root]');
  const tabRail = drawer?.querySelector('.rsv-mega-drawer__tabs');
  const shopToggle = drawer?.querySelector('[data-rsv-mobile-shop-toggle]');
  const panels = drawer?.querySelector('.rsv-mega-drawer__panels');
  const shopPanel = drawer?.querySelector('[data-rsv-mega-panel-content]');
  const activeIndicator = drawer?.querySelector('.rsv-mega-drawer__active-indicator');
  const pageTabs = [...(drawer?.querySelectorAll('[data-rsv-mobile-page-tab]') || [])];
  const profileCards = [...(drawer?.querySelectorAll('.rsv-mega-drawer__grid-card') || [])];
  const profileGrids = [...(drawer?.querySelectorAll('.rsv-mega-drawer__grid') || [])];
  const mobileCta = section.querySelector('[data-rsv-architectural-mobile-cta]');
  let ctaFrame = 0;
  let revealTimer = 0;
  let ctaHoldUntil = 0;
  let lockedScrollY = null;
  let bodyInlineSnapshot = null;
  let htmlOverflowSnapshot = '';
  let htmlScrollBehaviorSnapshot = '';
  let lastTouchY = null;
  let touchStartedInScrollArea = false;
  let navigationPending = false;
  let interactionTimer = 0;
  let navigationTimer = 0;

  section.style.removeProperty('--rsv-mobile-drawer-offset');
  drawerHead?.style.removeProperty('--rsv-mobile-close-anchor-top');
  drawerHead?.style.removeProperty('--rsv-mobile-close-anchor-left');
  const ensureDrawerSemantics = () => {
    const drawerSurface = drawer?.querySelector('[data-rsv-mega-drawer]');
    if (!(drawerSurface instanceof HTMLElement)) return;
    drawerSurface.setAttribute('role', 'dialog');
    drawerSurface.setAttribute('aria-modal', 'true');
    if (!drawerSurface.hasAttribute('aria-label')) {
      drawerSurface.setAttribute('aria-label', 'Main menu');
    }
  };
  ensureDrawerSemantics();

  const focusWithoutScroll = (element) => {
    if (!(element instanceof HTMLElement)) return;
    try {
      element.focus({ preventScroll: true });
    } catch (_error) {
      element.focus();
    }
  };

  const moveActiveIndicator = (target, visible = true) => {
    if (
      !(tabRail instanceof HTMLElement) ||
      !(activeIndicator instanceof HTMLElement) ||
      !(target instanceof HTMLElement)
    ) {
      return;
    }

    const label = target.querySelector('.rsv-mega-drawer__tab-label');
    const labelWidth =
      label instanceof HTMLElement ? Math.ceil(label.getBoundingClientRect().width) : 0;
    const indicatorWidth = Math.max(0, labelWidth || target.offsetWidth - 12);
    const indicatorLeft = target.offsetLeft + Math.max(0, (target.offsetWidth - indicatorWidth) / 2);
    activeIndicator.style.width = `${indicatorWidth}px`;
    activeIndicator.style.transform = `translate3d(${indicatorLeft}px, 0, 0)`;
    activeIndicator.style.opacity = visible ? '1' : '0';
  };

  const setShopExpanded = (expanded) => {
    if (!(shopToggle instanceof HTMLElement) || !(shopPanel instanceof HTMLElement)) return;

    shopToggle.classList.toggle('is-active', expanded);
    shopToggle.setAttribute('aria-expanded', String(expanded));
    shopToggle.setAttribute('aria-selected', String(expanded));
    shopPanel.classList.toggle('is-active', expanded);
    shopPanel.toggleAttribute('hidden', !expanded);
    panels?.classList.toggle('is-collapsed', !expanded);
    pageTabs.forEach((tab) => {
      tab.classList.remove('is-active');
      tab.removeAttribute('aria-current');
    });
    moveActiveIndicator(shopToggle, expanded);

    if (expanded && tabRail instanceof HTMLElement) {
      const targetLeft =
        shopToggle.offsetLeft - (tabRail.clientWidth - shopToggle.offsetWidth) / 2;
      tabRail.scrollTo({
        left: Math.max(0, targetLeft),
        behavior: reducedMotionQuery.matches ? 'auto' : 'smooth',
      });
    }
  };

  const resetRouteState = () => {
    navigationPending = false;
    window.clearTimeout(interactionTimer);
    window.clearTimeout(navigationTimer);
    interactionTimer = 0;
    navigationTimer = 0;
    profileGrids.forEach((grid) => grid.classList.remove('is-routing'));
    profileCards.forEach((card) => {
      card.classList.remove('is-selecting');
      card.removeAttribute('aria-current');
    });
  };

  const lockPage = () => {
    if (!drawerQuery.matches || lockedScrollY !== null) return;

    lockedScrollY = Math.max(0, window.scrollY);
    htmlOverflowSnapshot = document.documentElement.style.overflow;
    htmlScrollBehaviorSnapshot = document.documentElement.style.scrollBehavior;
    bodyInlineSnapshot = {
      position: document.body.style.position,
      top: document.body.style.top,
      right: document.body.style.right,
      left: document.body.style.left,
      width: document.body.style.width,
      overflow: document.body.style.overflow,
    };

    document.documentElement.style.overflow = 'hidden';
    document.documentElement.style.scrollBehavior = 'auto';
    document.body.style.position = 'fixed';
    document.body.style.top = `${-lockedScrollY}px`;
    document.body.style.right = '0';
    document.body.style.left = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    document.body.setAttribute(PAGE_LOCK_ATTRIBUTE, 'true');
  };

  const unlockPage = () => {
    if (lockedScrollY === null || bodyInlineSnapshot === null) return;

    const restoreY = lockedScrollY;
    document.documentElement.style.overflow = htmlOverflowSnapshot;
    document.body.style.position = bodyInlineSnapshot.position;
    document.body.style.top = bodyInlineSnapshot.top;
    document.body.style.right = bodyInlineSnapshot.right;
    document.body.style.left = bodyInlineSnapshot.left;
    document.body.style.width = bodyInlineSnapshot.width;
    document.body.style.overflow = bodyInlineSnapshot.overflow;
    document.body.removeAttribute(PAGE_LOCK_ATTRIBUTE);

    lockedScrollY = null;
    bodyInlineSnapshot = null;
    window.scrollTo(0, restoreY);
    window.requestAnimationFrame(() => {
      document.documentElement.style.scrollBehavior = htmlScrollBehaviorSnapshot;
      revealChrome({ delay: 0 });
      scheduleCtaTop();
    });
  };

  const scheduleCtaTop = () => {
    if (!(mobileCta instanceof HTMLElement) || ctaFrame) return;

    ctaFrame = window.requestAnimationFrame(() => {
      ctaFrame = 0;
      if (!phoneQuery.matches) return;

      const currentY = Math.max(0, window.scrollY);
      const naturalSectionTop = Math.max(0, section.offsetTop - currentY);
      const nextTop = Math.round(naturalSectionTop + header.getBoundingClientRect().height + 8);
      mobileCta.style.setProperty('--rsv-arch-mobile-cta-top', `${nextTop}px`);
    });
  };

  const setState = (nextState) => {
    if (section.getAttribute(STATE_ATTRIBUTE) !== nextState) {
      section.setAttribute(STATE_ATTRIBUTE, nextState);
    }
  };

  const clearRevealTimer = () => {
    if (!revealTimer) return;
    window.clearTimeout(revealTimer);
    revealTimer = 0;
  };

  const revealChrome = ({ delay = HEADER_REVEAL_DELAY_MS } = {}) => {
    clearRevealTimer();
    scheduleCtaTop();

    const transitionDelay = reducedMotionQuery.matches ? 0 : delay;
    const holdDelay = reducedMotionQuery.matches ? 0 : Math.max(0, ctaHoldUntil - Date.now());
    const effectiveDelay = Math.max(transitionDelay, holdDelay);
    if (effectiveDelay === 0) {
      section.removeAttribute(CTA_HOLD_ATTRIBUTE);
      setState('visible');
      return;
    }

    section.setAttribute(CTA_HOLD_ATTRIBUTE, 'true');
    setState('revealing');
    revealTimer = window.setTimeout(() => {
      revealTimer = 0;
      section.removeAttribute(CTA_HOLD_ATTRIBUTE);
      setState('visible');
      scheduleCtaTop();
    }, effectiveDelay);
  };

  const drawerIsOpen = () =>
    drawerQuery.matches && drawer instanceof HTMLDetailsElement && drawer.hasAttribute('open');

  const shouldContainScroll = (deltaY) => {
    if (!(drawerScrollArea instanceof HTMLElement)) return true;

    const maxScroll = Math.max(0, drawerScrollArea.scrollHeight - drawerScrollArea.clientHeight);
    if (maxScroll <= 1) return true;
    if (deltaY < 0 && drawerScrollArea.scrollTop <= 0) return true;
    if (deltaY > 0 && drawerScrollArea.scrollTop >= maxScroll - 1) return true;
    return false;
  };

  window.addEventListener(
    'resize',
    () => {
      revealChrome({ delay: 0 });
      scheduleCtaTop();
    },
    { passive: true }
  );
  window.addEventListener('pageshow', () => {
    revealChrome({ delay: 0 });
    scheduleCtaTop();
  });

  if (drawer instanceof HTMLDetailsElement) {
    const drawerTrigger = drawer.querySelector('summary');
    const beginDrawerClose = () => {
      ctaHoldUntil = Date.now() + DRAWER_CLOSE_DELAY_MS;
      revealChrome({ delay: DRAWER_CLOSE_DELAY_MS });
    };

    const closeDrawerSurface = () => {
      beginDrawerClose();
      if (drawerRoot && typeof drawerRoot.close === 'function') {
        drawerRoot.close();
      } else {
        drawer.querySelector('.rsv-mega-drawer__close')?.click();
      }
      focusWithoutScroll(drawerTrigger);
    };

    const scheduleRouteNavigation = (link, selectionDelay) => {
      const closeDelay = reducedMotionQuery.matches ? 0 : selectionDelay;
      const routeDelay =
        closeDelay + (reducedMotionQuery.matches ? 16 : DRAWER_CLOSE_DELAY_MS + 32);
      interactionTimer = window.setTimeout(closeDrawerSurface, closeDelay);
      navigationTimer = window.setTimeout(() => {
        unlockPage();
        window.location.assign(link.href);
      }, routeDelay);
    };

    shopToggle?.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
        if (navigationPending) return;
        setShopExpanded(shopToggle.getAttribute('aria-expanded') !== 'true');
      },
      { capture: true }
    );

    pageTabs.forEach((tab) => {
      tab.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (navigationPending || !(tab instanceof HTMLAnchorElement)) return;
          navigationPending = true;
          shopToggle?.classList.remove('is-active');
          pageTabs.forEach((candidate) => {
            const selected = candidate === tab;
            candidate.classList.toggle('is-active', selected);
            candidate.toggleAttribute('aria-current', selected);
            if (selected) candidate.setAttribute('aria-current', 'page');
          });
          moveActiveIndicator(tab);
          scheduleRouteNavigation(tab, 160);
        },
        { capture: true }
      );
    });

    profileCards.forEach((card, index) => {
      card.style.setProperty('--rsv-card-index', String(index));
      card.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          if (navigationPending || !(card instanceof HTMLAnchorElement)) return;
          navigationPending = true;
          const grid = card.closest('.rsv-mega-drawer__grid');
          grid?.classList.add('is-routing');
          card.classList.add('is-selecting');
          card.setAttribute('aria-current', 'page');
          scheduleRouteNavigation(card, 120);
        },
        { capture: true }
      );
    });

    drawerTrigger?.addEventListener(
      'click',
      () => {
        if (!drawer.hasAttribute('open')) lockPage();
        revealChrome({ delay: 0 });
      },
      { capture: true }
    );

    document.addEventListener(
      'wheel',
      (event) => {
        if (!drawerIsOpen()) return;

        const target = event.target;
        const isInsideScrollArea =
          target instanceof Element && target.closest('.rsv-mega-drawer__scrollarea');
        if (isInsideScrollArea && !shouldContainScroll(event.deltaY)) return;
        event.preventDefault();
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'touchstart',
      (event) => {
        if (!drawerIsOpen() || event.touches.length !== 1) {
          lastTouchY = null;
          touchStartedInScrollArea = false;
          return;
        }

        const target = event.target;
        touchStartedInScrollArea =
          target instanceof Element &&
          Boolean(target.closest('.rsv-mega-drawer__scrollarea'));
        lastTouchY = event.touches[0].clientY;
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'touchmove',
      (event) => {
        if (!drawerIsOpen() || event.touches.length !== 1 || lastTouchY === null) return;

        const currentTouchY = event.touches[0].clientY;
        const deltaY = lastTouchY - currentTouchY;
        lastTouchY = currentTouchY;
        if (!touchStartedInScrollArea || shouldContainScroll(deltaY)) event.preventDefault();
      },
      { capture: true, passive: false }
    );

    document.addEventListener(
      'touchend',
      () => {
        lastTouchY = null;
        touchStartedInScrollArea = false;
      },
      { capture: true, passive: true }
    );
    document.addEventListener(
      'touchcancel',
      () => {
        lastTouchY = null;
        touchStartedInScrollArea = false;
      },
      { capture: true, passive: true }
    );

    document.addEventListener(
      'click',
      (event) => {
        const target = event.target;
        if (!target || typeof target.closest !== 'function') return;
        if (target.closest('.rsv-mega-drawer__close, .rsv-mega-drawer__backdrop')) {
          beginDrawerClose();
          window.requestAnimationFrame(() => focusWithoutScroll(drawerTrigger));
        }
      },
      { capture: true }
    );

    document.addEventListener('keyup', (event) => {
      if (event.key === 'Escape' && drawer.classList.contains('menu-open')) {
        beginDrawerClose();
        window.requestAnimationFrame(() => focusWithoutScroll(drawerTrigger));
      }
    });

    const observer = new MutationObserver(() => {
      if (drawer.classList.contains('menu-open')) {
        ensureDrawerSemantics();
        resetRouteState();
        setShopExpanded(true);
        lockPage();
        ctaHoldUntil = 0;
        revealChrome({ delay: 0 });
      } else if (drawer.hasAttribute('open')) {
        ctaHoldUntil = Date.now() + DRAWER_CLOSE_DELAY_MS;
        revealChrome({ delay: DRAWER_CLOSE_DELAY_MS });
      } else {
        unlockPage();
      }
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ['class', 'open'] });

    if (drawer.hasAttribute('open')) {
      ensureDrawerSemantics();
      resetRouteState();
      setShopExpanded(true);
      lockPage();
    }

    if (tabRail instanceof HTMLElement) {
      const syncIndicator = () => {
        const activeTarget =
          tabRail.querySelector('.rsv-mega-drawer__tab.is-active') || shopToggle;
        moveActiveIndicator(activeTarget, Boolean(activeTarget));
      };
      tabRail.addEventListener('scroll', syncIndicator, { passive: true });
      window.addEventListener('resize', syncIndicator, { passive: true });
      new ResizeObserver(syncIndicator).observe(tabRail);
      window.requestAnimationFrame(syncIndicator);
    }
  }

  reducedMotionQuery.addEventListener('change', () => {
    revealChrome({ delay: 0 });
  });

  drawerQuery.addEventListener('change', (event) => {
    if (!event.matches) unlockPage();
    revealChrome({ delay: 0 });
  });

  phoneQuery.addEventListener('change', () => revealChrome({ delay: 0 }));

  window.addEventListener('pagehide', () => {
    resetRouteState();
    if (drawer instanceof HTMLDetailsElement) {
      drawer.classList.remove('menu-open');
      drawer.removeAttribute('open');
      drawer.querySelector('summary')?.setAttribute('aria-expanded', 'false');
    }
    unlockPage();
  });

  revealChrome({ delay: 0 });
}

function init() {
  document.querySelectorAll(HEADER_SELECTOR).forEach(initMobileNavigation);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}

document.addEventListener('shopify:section:load', init);
